/**
 * THE QUOTE AS SOMETHING YOU CAN PASTE INTO A TEXT MESSAGE.
 *
 * Half of this trade is quoted by text, not by email and not on paper. A
 * homeowner standing in a driveway wants the number on their phone before the
 * truck pulls away, and a PDF attachment is three taps and a preview that may
 * or may not open. So the same document has a second form: plain text, no
 * markup, no table alignment that collapses the moment it lands in a message
 * app with a different font.
 *
 * It says exactly what the page says and never more. A text quote that leaves
 * out the discount, or rounds differently from the PDF, is how a customer ends
 * up holding two different numbers with your name on both.
 */

import type { Company } from './company'
import type { QuoteLines } from './quote'
import { CATEGORY_SETUP } from '../store/useEditor'
import type { Paper } from './paper'
import {
  balanceDue,
  depositAmount,
  discount,
  discountLabel,
  docNumber,
  longDate,
  money,
  MONTHS,
  offFraction,
  priceCents,
  subtotal,
  total,
  totalAt,
} from './paper'

export interface TextJob {
  customer: string
  address: string
  name: string
}

export function quoteText(
  company: Company,
  job: TextJob,
  lines: QuoteLines,
  paper: Paper,
): string {
  const out: string[] = []
  const off = offFraction(paper)
  const invoice = paper.kind === 'invoice'
  const paid = invoice && !!paper.deposit.paidAt
  const monthWord = paper.installMonth == null ? '' : MONTHS[paper.installMonth]

  const head = [company.name, company.phone].filter((x) => x.trim())
  if (head.length) out.push(head.join('  '), '')

  const number = docNumber(paper, company.prefix)
  out.push(`${invoice ? 'INVOICE' : 'QUOTE'}${number ? ` ${number}` : ''}`)
  out.push(longDate(paper.date))
  if (monthWord) out.push(`${monthWord} install`)
  out.push('')

  const who = [job.customer || job.name, ...job.address.split('\n'), paper.customerPhone]
    .map((x) => x.trim())
    .filter(Boolean)
  if (who.length) out.push(...who, '')

  for (const l of lines) {
    const label = l.label?.trim() || CATEGORY_SETUP[l.category].label
    const lit = l.lighting.trim() ? ` (${l.lighting.trim()})` : ''
    out.push(`${label}${lit}  ${money(priceCents(l, off) / 100)}`)
    if (l.note.trim()) out.push(`   ${l.note.trim()}`)
  }
  out.push('')

  const disc = discount(lines, paper)
  if (disc > 0) {
    out.push(`Subtotal  ${money(subtotal(lines))}`)
    out.push(`${discountLabel(paper)}  -${money(disc)}`)
  }
  if (paid) out.push(`Deposit received  -${money(depositAmount(lines, paper))}`)
  out.push(
    `${paid ? 'BALANCE DUE' : invoice ? 'AMOUNT DUE' : 'TOTAL'}  ${money(
      paid ? balanceDue(lines, paper) : total(lines, paper),
    )}`,
  )

  if (paper.showLadder && paper.tiers.length > 0 && subtotal(lines) > 0) {
    out.push('', 'Install month pricing')
    for (const t of paper.tiers) {
      const label = t.offPct > 0 ? `${t.offPct}% off` : 'full price'
      out.push(`${MONTHS[t.month]}  ${label}  ${money(totalAt(lines, t.offPct))}`)
    }
  }

  const terms = [paper.termsLeft, paper.termsRight].map((x) => x.trim()).filter(Boolean)
  if (terms.length) out.push('', ...terms)

  return out.join('\n')
}
